import { Tooltip } from '@melements/front-components';
import React, { useContext, useState } from 'react';
import { Field, FieldRenderProps, Form, FormRenderProps } from 'react-final-form';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormContent, Input, LocaleContext, ReCaptchaPolicy } from 'shared';
import { combineValidators, isRepeatTheSame, required, validatePassword } from 'utils/formValidators';

import tooltipIcon from '../../../assets/icons/info-deep-blue.svg';
import {
  FieldWithTooltip,
  RegistrationTooltip,
} from '../../authorization/registration/registrationForm/RegistrationForm.styles';

import { SubmitButton } from './ChangePasswordForm.styles';
import { ChangePasswordFormProps } from './ChangePasswordForm.types';

export const ChangePasswordForm = ({ onSubmit, isSubmitFailed }: ChangePasswordFormProps) => {
  const localeContext = useContext(LocaleContext);

  const [isCaptchaFilled, setCaptchaFilled] = useState(false);

  const handleVerifyCaptcha = (change: (name: string, value?: any) => void) => (response: string) => {
    change('captchaResponse', response);
    setCaptchaFilled(true);
  };

  return (
    <FormContent className="form change-password-form">
      <Form onSubmit={onSubmit} validateOnBlur={false}>
        {({ handleSubmit, submitting, form: { change } }: FormRenderProps) => {
          return (
            <form onSubmit={handleSubmit} action="">
              <FieldWithTooltip>
                <Field
                  name="password"
                  validate={combineValidators(
                    required(localeContext.formatMessage({ id: 'FORM.REQUIRED' })),
                    validatePassword(localeContext.formatMessage({ id: 'FORM.INVALID-PASSWORD' })),
                  )}
                >
                  {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                    <Input
                      label={localeContext.formatMessage({ id: 'CHANGE-PASSWORD.FORM.PASSWORD' })}
                      input={{ ...input, type: 'password' }}
                      meta={{
                        ...meta,
                        submitError: isSubmitFailed
                          ? localeContext.formatMessage({ id: 'CHANGE-PASSWORD.FORM.FAILED' })
                          : meta.submitError,
                      }}
                      className="change-password-form__password"
                    />
                  )}
                </Field>
                <Tooltip
                  content={
                    <RegistrationTooltip>
                      {localeContext.formatMessage({ id: 'FORM.PASSWORD-TIP' })}
                    </RegistrationTooltip>
                  }
                >
                  {({ ref }) => <img ref={ref} src={tooltipIcon} alt="" />}
                </Tooltip>
              </FieldWithTooltip>

              <Field
                name="repeatPassword"
                validate={isRepeatTheSame(
                  localeContext.formatMessage({ id: 'CHANGE-PASSWORD.FORM.NOT-MATCH' }),
                  'password',
                )}
              >
                {({ input, meta }: FieldRenderProps<HTMLInputElement>) => (
                  <Input
                    label={localeContext.formatMessage({ id: 'CHANGE-PASSWORD.FORM.REPEAT-PASSWORD' })}
                    input={{ ...input, type: 'password' }}
                    meta={meta}
                    className="change-password-form__repeat-password"
                  />
                )}
              </Field>

              <GoogleReCaptcha onVerify={handleVerifyCaptcha(change)} />

              <ReCaptchaPolicy />

              <SubmitButton
                type="leading"
                className="change-password-form__submit"
                disabled={submitting || !isCaptchaFilled}
              >
                {localeContext.formatMessage({ id: 'CHANGE-PASSWORD.FORM.CONFIRM' })}
              </SubmitButton>
            </form>
          );
        }}
      </Form>
    </FormContent>
  );
};
